import { useState } from 'react';
import { getApiErrorMessage } from '@shared/utils/apiError';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Save, X, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { clinicApi } from '../services/clinicApi';
import type { ClinicDetailDto, UpdateClinicOnboardingRequest } from '../types/clinic';
import RequirePermission from '@shared/components/RequirePermission';
import './ClinicInfoSection.css';

interface ClinicExternalSystemsSectionProps {
  clinic: ClinicDetailDto;
  onUpdate: () => void;
}

interface EditFormState {
  eScreenCode: string;
  formFoxCode: string;
  questCode: string;
}

function buildFormState(clinic: ClinicDetailDto): EditFormState {
  return {
    eScreenCode: clinic.eScreenCode ?? '',
    formFoxCode: clinic.formFoxCode ?? '',
    questCode: clinic.questCode ?? '',
  };
}

export default function ClinicExternalSystemsSection({ clinic, onUpdate }: Readonly<ClinicExternalSystemsSectionProps>) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [form, setForm] = useState<EditFormState>(() => buildFormState(clinic));
  const [showSuccess, setShowSuccess] = useState(false);

  // No prop-sync needed: view mode reads `clinic` directly, and `form` is
  // rebuilt from the latest clinic in handleEdit/handleCancel.

  // The onboarding proc overwrites every onboarding column, and this section
  // shares the endpoint with the Onboarding section — so the fields this
  // section doesn't edit are re-fetched at save time rather than taken from
  // the possibly stale `clinic` prop, or saving here could overwrite the
  // other section's just-saved edits.
  const updateMutation = useMutation({
    mutationFn: async () => {
      const fresh = await clinicApi.getClinicById(clinic.id);
      const request: UpdateClinicOnboardingRequest = {
        preferredMethodOfAppointment: fresh.preferredMethodOfAppointment,
        electronicChainOfCustody: fresh.electronicChainOfCustody,
        reportingMethod: fresh.reportingMethod,
        dotCertification: fresh.dotCertification,
        numberOfCertifiedDOTProfessionals: fresh.dotCertification ? fresh.numberOfCertifiedDOTProfessionals : undefined,
        w9OnFile: fresh.w9OnFile,
        // External system fields edited here
        eScreenCode: form.eScreenCode.trim() || undefined,
        formFoxCode: form.formFoxCode.trim() || undefined,
        questCode: form.questCode.trim() || undefined,
      };
      return clinicApi.updateClinicOnboarding(clinic.id, request);
    },
    onSuccess: () => {
      setIsEditing(false);
      setShowSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['clinic', clinic.id] });
      queryClient.invalidateQueries({ queryKey: ['clinics'] });
      onUpdate();
      setTimeout(() => setShowSuccess(false), 3000);
    },
  });

  const handleSave = () => {
    updateMutation.mutate();
  };

  const handleCancel = () => {
    setIsEditing(false);
    setForm(buildFormState(clinic));
    updateMutation.reset();
  };

  const handleEdit = () => {
    setForm(buildFormState(clinic));
    updateMutation.reset();
    setIsEditing(true);
  };

  const updateField = (field: keyof EditFormState, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const formatValue = (value: string | undefined | null) => {
    if (!value || value === '') {
      return 'Not Set';
    }
    return value;
  };

  return (
    <div className="info-section">
      {/* Section header with edit/save/cancel */}
      <div className="info-section-header">
        <button
          type="button"
          className="info-section-toggle"
          onClick={() => !isEditing && setIsCollapsed(c => !c)}
          aria-expanded={!isCollapsed}
          disabled={isEditing}
        >
          <ChevronDown size={16} className={`info-section-chevron ${isCollapsed ? 'collapsed' : ''}`} />
          <h3 className="info-section-title">External Systems</h3>
        </button>
        <div className="info-section-actions">
          {showSuccess && (
            <span className="info-success-toast">
              <CheckCircle size={14} />
              Saved
            </span>
          )}
          {isEditing ? (
            <>
              <button
                type="button"
                className="svc-header-btn svc-header-btn-cancel"
                onClick={handleCancel}
                disabled={updateMutation.isPending}
              >
                <X size={14} />
                Cancel
              </button>
              <button
                type="button"
                className="svc-header-btn svc-header-btn-save"
                onClick={handleSave}
                disabled={updateMutation.isPending}
              >
                <Save size={14} />
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            // Matches the roles PUT /clinics/{id}/onboarding accepts for
            // vendor-code fields (SLV2-2999 boundary).
            <RequirePermission anyOf={['UserCanEditClinicProfile', 'UserCanEditClinicCapabilities']}>
              <button
                type="button"
                className="svc-edit-btn"
                onClick={handleEdit}
                title="Edit external systems"
              >
                <Pencil size={14} />
                Edit
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      {!isCollapsed && (
        <>
          {updateMutation.isError && (
            <div className="info-save-error">
              <AlertCircle size={14} />
              {getApiErrorMessage(updateMutation.error, 'Failed to save changes')}
            </div>
          )}

          <div className="info-content">
            <div className="info-details">
              {/* Row 1: eScreen Code */}
              <div className="info-row">
                <div className="info-field">
                  <label htmlFor="clinic-escreen-code">eScreen Code</label>
                  {isEditing ? (
                    <input
                      id="clinic-escreen-code"
                      type="text"
                      className="info-input"
                      value={form.eScreenCode}
                      onChange={e => updateField('eScreenCode', e.target.value)}
                      placeholder="Enter eScreen platform identifier"
                    />
                  ) : (
                    <span>{formatValue(clinic.eScreenCode)}</span>
                  )}
                </div>
              </div>
              {/* Row 2: FormFox Code */}
              <div className="info-row">
                <div className="info-field">
                  <label htmlFor="clinic-formfox-code">FormFox Code</label>
                  {isEditing ? (
                    <input
                      id="clinic-formfox-code"
                      type="text"
                      className="info-input"
                      value={form.formFoxCode}
                      onChange={e => updateField('formFoxCode', e.target.value)}
                      placeholder="Enter FormFox platform identifier"
                    />
                  ) : (
                    <span>{formatValue(clinic.formFoxCode)}</span>
                  )}
                </div>
              </div>
              {/* Row 3: Quest Code */}
              <div className="info-row">
                <div className="info-field">
                  <label htmlFor="clinic-quest-code">Quest Code</label>
                  {isEditing ? (
                    <input
                      id="clinic-quest-code"
                      type="text"
                      className="info-input"
                      value={form.questCode}
                      onChange={e => updateField('questCode', e.target.value)}
                      placeholder="Enter Quest platform identifier"
                    />
                  ) : (
                    <span>{formatValue(clinic.questCode)}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
